/**
 * RemoteOperationsPage — "פעולות מרחוק" (DIGEMP-REMOTE-OPERATIONS-V2-FRONTEND-080).
 *
 * The operator's remote-session cockpit for one Digital Employee at a time. Before and during a
 * remote session it makes the seven decision facts legible on one screen:
 *   exact target · connection · approval requirement · allowed mode · current stage ·
 *   evidence · verified completion state.
 *
 * Read-only by construction. Every value comes from existing agents_view-gated endpoints — the
 * employee workspace, the run detail, the run list, approval tokens, connections health,
 * crisis mode and the audited experience history. This screen starts no session, tests no
 * connection, approves nothing and re-implements no permission gate: each next action links to
 * the run/ticket/conversation screen where the existing gates (agents_approve for approvals,
 * agents_run for execution controls) already apply.
 *
 * Truthfulness rules:
 *   - A session is "live" only under the manager flow's own reuse rule (status session_created,
 *     created for THIS run, expiry in the future). Expired, foreign-run or failed sessions never
 *     read as success; they say what they are.
 *   - An explicit operator-supplied TeamViewer ID is the only green target; discovery in the
 *     managed inventory, or its absence, is amber and never green.
 *   - Unreadable evidence (crisis state without agents_manage, failed health/experience reads)
 *     renders as unknown — never as "off", never as "0", never as success.
 *   - Verified completion comes from the audited experience-history contract (technician
 *     resolution note / executed approved close), not from a model self-claim.
 *   - Only whitelisted bounded scalars of run.context are read (teamviewer_id, connection_state,
 *     connection_lookup.status, attended_session.{status,valid_until}, attended_session_blocked,
 *     conversation_id, technician). The customer link and session id stay on the run screen.
 *
 * Live-status vocabulary: the status words and tones come from the shared tables
 * (components/statusVocabulary.js) and the browser-offline signal from components/useLiveState.js.
 * Source states use the shared classifySource ladder — a failed refresh with a retained payload is
 * stale and labelled with its last successful load, a failed first load is unknown (never "no
 * active run"), and offline is stated instead of letting the page read as current.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentRunsApi, agentsApi, connectionsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META, drawnStatus } from "../components/EmployeeAvatar";
import { ExperienceTimeline } from "../components/EmployeeWorkspace";
import { CHIP_CLS, LIVE_STATUS_CLS, SOURCE_STATES, classifySource, statusChipMeta } from "../components/statusVocabulary";
import { classifySourceTruth } from "../components/sourceTruth";
import SourceTruthBadge from "../components/SourceTruthBadge";
import { degradedAnalysisLabel } from "../components/degradedAnalysisVocabulary";
import { ACTION_TYPE_LABEL } from "../components/actionTypeVocabulary";
import { riskGradeLabel } from "../components/riskGradeVocabulary";
import { operationTypeLabel } from "../components/operationTypeVocabulary";
import { useOnlineStatus } from "../components/useLiveState";
import { parseUtc } from "../utils/utcDate";

// ─── Glossaries (mirror the labels the run screens already use) ───────────────

const RUN_STATUS_HE = {
  pending: "ממתין להתחלה",
  analyzing: "מנתח את הקריאה",
  awaiting_technician_review: "ממתין לסקירת טכנאי",
  awaiting_approval: "ממתין לאישור",
  waiting_for_customer_connection: "ממתין לחיבור הלקוח",
  connection_active: "חיבור פעיל",
  waiting_for_customer_answer: "ממתין לתשובת הלקוח",
  diagnosing: "מאבחן",
  executing: "מבצע",
  approved: "אושר — ממתין לביצוע",
  rejected: "נדחה",
  paused: "הושהה",
  resolved: "נפתר",
  completed: "הושלם",
  failed: "נכשל",
};

const TERMINAL_RUN_STATUSES = new Set(["completed", "resolved", "failed", "rejected"]);

const TIER_HE = {
  tier_1: { label: "רמה 1", meaning: "צפייה בלבד — אין ביצוע ללא הרשאה ואישור אנושי" },
  tier_2: { label: "רמה 2", meaning: "תפעולי — פעולות מרשימה מאושרת, באישור אנושי" },
  tier_3: { label: "רמה 3", meaning: "מורשה — אישור חזק לפעולות קריטיות" },
};

const AUTONOMY_HE = {
  approval_required: "כל הודעה ללקוח וכל שינוי מצב דורשים אישור אדם",
  supervised: "כל הודעה ללקוח וכל שינוי מצב דורשים אישור אדם",
  semi_autonomous: "קריאה בלבד אוטונומית; כתיבה דורשת אישור",
  autonomous: "כפוף למדיניות הפעולה לפי כלי — אין מסלול אוטונומי כללי",
};

const ACTION_OUTCOME_HE = {
  proposed: { label: "הוצע", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  approved: { label: "אושר", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  rejected: { label: "נדחה", cls: "bg-red-50 text-red-700 border-red-200" },
  succeeded: { label: "הצליח", cls: "bg-green-50 text-green-700 border-green-200" },
  failed: { label: "נכשל", cls: "bg-red-50 text-red-700 border-red-200" },
};

const APPROVAL_TOKEN_STATUS = {
  revoked: { label: "בוטל", cls: "bg-red-50 text-red-700 border-red-200" },
  used: { label: "נוצל", cls: "bg-green-50 text-green-700 border-green-200" },
  expired: { label: "פג תוקף", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  pending: { label: "בתוקף — טרם נוצל", cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

const CONNECTION_STATUS_META = {
  ok: { label: "נבדק בהצלחה", cls: "bg-green-100 text-green-700 border-green-200" },
  error: { label: "הבדיקה נכשלה", cls: "bg-red-100 text-red-700 border-red-200" },
  failed: { label: "הבדיקה נכשלה", cls: "bg-red-100 text-red-700 border-red-200" },
};

const LOOKUP_STATUS_HE = {
  managed_device_found: "המכשיר אותר במלאי המנוהל — לא סופק מזהה מפורש",
  managed_device_not_found: "המכשיר לא אותר במלאי המנוהל",
  teamviewer_id_missing: "לא סופק מזהה TeamViewer",
  lookup_failed: "איתור המכשיר נכשל",
  blocked_policy_inventory_selection: "בחירה מהמלאי חסומה במדיניות — נדרש מזהה מפורש",
};

const COMPLETION_META = {
  verified_success: { icon: "✓", label: "הושלם ואומת", cls: "bg-green-100 text-green-700 border-green-200" },
  unverified: { icon: "◷", label: "הושלם — טרם אומת", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  failed: { icon: "✕", label: "נכשל", cls: "bg-red-100 text-red-700 border-red-200" },
  cancelled: { icon: "⊘", label: "בוטל / נדחה", cls: "bg-slate-200 text-slate-700 border-slate-300" },
};

const VERIFICATION_BASIS_HE = {
  technician_resolution_outcome: "אימות טכנאי — הערת פתרון מתועדת",
  approved_close_executed: "סגירה מאושרת שבוצעה",
};

// ─── Session stages (the canonical remote-session spine) ─────────────────────

export const SESSION_STAGES = [
  { key: "requested", label: "בקשה", hint: "ריצת עבודה נוצרה לקריאה" },
  { key: "analysis", label: "ניתוח", hint: "הבנת הקריאה והצעת מהלך" },
  { key: "approval", label: "אישור אנושי", hint: "החלטת טכנאי לפני ביצוע" },
  { key: "target", label: "יעד מאושר", hint: "מזהה TeamViewer מפורש מהיעד" },
  { key: "session", label: "סשן מרחוק", hint: "הכנת החיבור והמתנה ללקוח" },
  { key: "execution", label: "ביצוע מוגבל", hint: "פעולות מאושרות ומוגבלות בלבד" },
  { key: "verification", label: "אימות והשלמה", hint: "ראיות סיום ואימות טכנאי" },
];

// Stage-tracker tones for the two states the shared vocabulary owns (blocked, unknown) come from
// its table; done/current/pending are tracker-progress states that have no shared key.
const STAGE_STATE_META = {
  done: { label: "הושלם", glyph: "✓", cls: "bg-green-100 text-green-700 border-green-300" },
  current: { label: "בשלב זה", glyph: "▶", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  pending: { label: "ממתין", glyph: "○", cls: "bg-gray-100 text-gray-500 border-gray-200" },
  blocked: { label: "חסום", glyph: "✕", cls: LIVE_STATUS_CLS.blocked },
  unknown: { label: "לא ידוע", glyph: "?", cls: LIVE_STATUS_CLS.unknown },
};

// How far a happy-path run status has carried the spine (index into SESSION_STAGES): stages
// before this index are filled "done" and the per-stage refinements below override from
// evidence. failed/rejected are handled explicitly — their furthest point is evidence-driven.
const FURTHEST_STAGE_BY_STATUS = {
  pending: 0,
  analyzing: 1,
  awaiting_technician_review: 2,
  awaiting_approval: 2,
  paused: 2,
  approved: 3,
  waiting_for_customer_connection: 4,
  connection_active: 5,
  diagnosing: 5,
  waiting_for_customer_answer: 5,
  executing: 5,
  resolved: 6,
  completed: 6,
};

const asText = (value) => {
  if (value == null) return null;
  if (typeof value === "string") return value.trim() ? value : null;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
};

function boundedText(value, max) {
  const text = asText(value);
  if (!text) return null;
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function fmtDateTime(iso) {
  if (!iso) return null;
  const parsed = parseUtc(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  try {
    return parsed.toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return null;
  }
}

/** The manager flow's own session-reuse rule, mirrored: only a session created for THIS run with
 * a parseable future expiry counts as live. Anything else is not reused by the backend either. */
export function isLiveAttendedSession(session, runId, now = Date.now()) {
  if (!session || typeof session !== "object" || Array.isArray(session)) return false;
  if (session.status !== "session_created") return false;
  if (!runId || session.run_id !== String(runId)) return false;
  const validUntil = asText(session.valid_until);
  if (!validUntil) return false;
  const parsed = parseUtc(validUntil);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getTime() > now;
}

/** Whitelisted session/target facts of a run. Raw run.context is never rendered anywhere. */
export function readRunFacts(run) {
  if (!run) return null;
  const ctx = run.context && typeof run.context === "object" && !Array.isArray(run.context) ? run.context : {};
  const session = ctx.attended_session && typeof ctx.attended_session === "object" ? ctx.attended_session : null;
  return {
    status: asText(run.status),
    runId: asText(run.id),
    teamviewerId: asText(ctx.teamviewer_id),
    connectionState: asText(ctx.connection_state),
    lookupStatus: asText(ctx.connection_lookup?.status),
    session: session
      ? {
          status: asText(session.status),
          validUntil: asText(session.valid_until),
          runId: asText(session.run_id),
        }
      : null,
    sessionBlocked: asText(ctx.attended_session_blocked),
    conversationId: asText(ctx.conversation_id),
    technician: boundedText(ctx.technician, 80),
    hasAnalysis: Boolean(run.analysis),
  };
}

/** Live / not-live classification of the recorded session, with the reason when not live. */
function liveStateOf(facts, now) {
  if (!facts) return { live: false, reason: "none" };
  const { session, runId } = facts;
  if (!session) return { live: false, reason: facts.sessionBlocked === "crisis_mode" ? "crisis" : "none" };
  if (facts.sessionBlocked === "crisis_mode") return { live: false, reason: "crisis" };
  if (session.status === "session_creation_failed") return { live: false, reason: "creation_failed" };
  if (session.status !== "session_created") return { live: false, reason: "none" };
  const asContract = { status: session.status, run_id: session.runId, valid_until: session.validUntil };
  if (isLiveAttendedSession(asContract, runId, now)) return { live: true, reason: null };
  const parsed = session.validUntil ? parseUtc(session.validUntil) : null;
  if (parsed && !Number.isNaN(parsed.getTime()) && parsed.getTime() <= now) return { live: false, reason: "expired" };
  // Foreign run, missing run_id or unparseable/invalid expiry — never treated as live.
  return { live: false, reason: "stale" };
}

/**
 * Derive the seven session stages from the run's own audited record. Pure and exported so the
 * truthfulness rules are directly testable. Returns `{ stages, currentKey }` where `currentKey`
 * is the first stage that is in progress or blocked (null when only done/pending remain).
 */
export function buildSessionStages(run, now = Date.now()) {
  if (!run) {
    return {
      stages: SESSION_STAGES.map((def, i) => ({
        ...def,
        state: "pending",
        note: i === 0 ? "אין ריצת עבודה פעילה לעובד זה כרגע" : null,
      })),
      currentKey: null,
    };
  }

  const facts = readRunFacts(run);
  const status = facts.status;
  const liveState = liveStateOf(facts, now);
  const actions = Array.isArray(run.actions) ? run.actions : [];
  const hasExecuted = actions.some((a) => a?.outcome === "succeeded");
  const hasApproval = hasExecuted || actions.some((a) => a?.outcome === "approved");
  const sessionInvolved = Boolean(facts.session) || facts.connectionState != null;
  const stages = SESSION_STAGES.map((def) => ({ ...def, state: "pending", note: null }));
  const set = (key, state, note = null) => {
    const stage = stages.find((s) => s.key === key);
    stage.state = state;
    stage.note = note;
  };
  // The analysis stage's own note. A degraded analysis's confidence is the fallback's derived number
  // (`helpdesk_analyst.py`: MockAnalysisAdapter, capped at 70 so it reads as a real "medium" signal
  // rather than as "nothing ran"), and this spine is what an operator debugs a failed session from —
  // so the note names the degradation instead of a confidence nothing measured. The figure is dropped
  // rather than disclosed, the same choice `manager_agent._analysis_complete_description` makes for
  // the stored audit row, because the note is a claim that the analysis stage completed.
  const confidenceNote = () => {
    const degraded = degradedAnalysisLabel(run.analysis?.degraded);
    if (degraded) return degraded;
    const confidence = run.analysis?.confidence;
    return Number.isFinite(confidence) ? `ביטחון ${confidence}%` : null;
  };
  const explicitIdNote = () => `מזהה שסופק מפורשות: ${facts.teamviewerId}`;

  // Stage 1 — request. The run exists.
  const createdLabel = fmtDateTime(run.created_at);
  set("requested", "done", createdLabel ? `נוצרה ${createdLabel}` : null);

  if (status === "failed") {
    // The failure point is where the evidence is missing, not a fixed index on the spine.
    set("analysis", facts.hasAnalysis ? "done" : "unknown",
      facts.hasAnalysis ? confidenceNote() : "לא תועד ניתוח לריצה זו");
    set("approval", hasApproval ? "done" : "unknown",
      hasApproval ? null : "לא תועד אישור אנושי לריצה זו");
    set("target", facts.teamviewerId ? "done" : "unknown",
      facts.teamviewerId ? explicitIdNote() : "לא תועד מזהה TeamViewer לריצה זו");
    if (liveState.live || facts.connectionState === "active") {
      set("session", "done", facts.connectionState === "active" ? "חיבור פעיל" : "סשן מתועד בתוקף");
    } else {
      set("session", "unknown", "לא תועדו ראיות סשן לריצה זו");
    }
    set("execution", "blocked", "הביצוע נכשל");
    set("verification", "pending");
  } else if (status === "rejected") {
    set("analysis", facts.hasAnalysis ? "done" : "unknown",
      facts.hasAnalysis ? confidenceNote() : "לא תועד ניתוח לריצה זו");
    set("approval", "blocked", "נדחה על ידי טכנאי — העבודה לא תמשיך");
    set("target", "pending");
    set("session", "pending");
    set("execution", "pending");
    set("verification", "pending");
  } else {
    const furthest = FURTHEST_STAGE_BY_STATUS[status] ?? 0;
    for (let i = 1; i < furthest; i += 1) {
      stages[i].state = "done";
    }

    // Stage 2 — analysis.
    if (facts.hasAnalysis) {
      set("analysis", "done", confidenceNote());
    } else if (furthest >= 2) {
      set("analysis", "unknown", "לא תועד ניתוח לריצה זו");
    } else if (status === "analyzing") {
      set("analysis", "current", "מנתח את הקריאה");
    } else {
      set("analysis", "pending");
    }

    // Stage 3 — human decision.
    if (status === "awaiting_technician_review") {
      set("approval", "current", "ממתין לסקירת טכנאי — אישור, דחייה או עריכה");
    } else if (status === "awaiting_approval") {
      set("approval", "current", "פעולה מוצעת ממתינה לאישור אנושי לפני ביצוע");
    } else if (status === "paused") {
      set("approval", "current", "הושהה — ממתין להחלטה אנושית: חידוש או סגירה");
    } else if (furthest < 3) {
      set("approval", "pending");
    } // furthest >= 3: already done

    // Stage 4 — exact target. An explicit operator-supplied ID is the only green state.
    if (facts.teamviewerId) {
      set("target", "done", explicitIdNote());
    } else if (furthest >= 3) {
      if (TERMINAL_RUN_STATUSES.has(status)) {
        if (sessionInvolved) {
          set("target", "unknown", "לא תועד מזהה TeamViewer מפורש לריצה זו");
        } else {
          set("target", "done", "לא נדרש יעד מרחוק — הריצה הסתיימה ללא תיעוד סשן");
        }
      } else {
        const lookupNote = facts.lookupStatus ? LOOKUP_STATUS_HE[facts.lookupStatus] : null;
        set("target", "current", lookupNote
          ? `${lookupNote} — נדרש מזהה מפורש לפני חיבור`
          : "לא סופק מזהה TeamViewer מפורשות — נדרש מסלול ידני לפני חיבור");
      }
    } else if (furthest === 2) {
      set("target", "pending", "ייקבע לפני הכנת החיבור");
    }

    // Stage 5 — remote session preparation.
    if (liveState.reason === "crisis") {
      set("session", "blocked", "יצירת הסשן נחסמה על-ידי מצב חירום");
    } else if (liveState.reason === "creation_failed") {
      set("session", "current", "יצירת הסשן נכשלה — ניתן להמשיך במסלול מזהה וסיסמה ידניים");
    } else if (liveState.reason === "expired") {
      set("session", "current", "סשן קודם פג תוקף — נדרשת הכנה מחדש");
    } else if (liveState.reason === "stale") {
      set("session", "current", "סשן מתועד שאינו תקף לריצה זו — נדרשת הכנה מחדש");
    } else if (liveState.live || facts.connectionState === "active") {
      const until = facts.session?.validUntil ? fmtDateTime(facts.session.validUntil) : null;
      set("session", "done", facts.connectionState === "active" ? "חיבור פעיל" : until ? `בתוקף עד ${until}` : null);
    } else if (facts.connectionState === "requested" || status === "waiting_for_customer_connection") {
      set("session", "current", "התבקש חיבור — ממתין לחיבור הלקוח");
    } else if (facts.connectionState === "not_required" || facts.connectionState === "complete") {
      set("session", "done", "לא נדרש חיבור מרחוק לריצה זו");
    } else if (furthest >= 5) {
      // already done via fill
    } else if (furthest >= 4) {
      set("session", "current", "ממתין לחיבור הלקוח");
    } else {
      set("session", "pending");
    }

    // Stage 6 — bounded execution.
    if (status === "connection_active") {
      set("execution", "current", "חיבור פעיל — האבחון יופעל");
    } else if (status === "diagnosing" || status === "executing") {
      set("execution", "current", "בביצוע — פעולות מאושרות ומוגבלות");
    } else if (status === "waiting_for_customer_answer") {
      set("execution", "current", "ממתין לתשובת הלקוח");
    } else if (status === "approved") {
      set("execution", "current", "אושר — ממתין לביצוע");
    } else if (TERMINAL_RUN_STATUSES.has(status)) {
      set("execution", "done", actions.length > 0 ? `${actions.length} פעולות מתועדות` : null);
    } else if (furthest < 5) {
      set("execution", "pending");
    }

    // Stage 7 — verification and completion.
    if (status === "completed" || status === "resolved") {
      if (facts.technician) {
        set("verification", "done", `אימות טכנאי מתועד (${facts.technician})`);
      } else if (status === "resolved") {
        set("verification", "current", "העובד מסמן שנפתר — ממתין לסגירת טכנאי");
      } else {
        set("verification", "current", "הושלם — טרם תועד אימות טכנאי");
      }
    } else {
      set("verification", "pending");
    }
  }

  const currentKey = (stages.find((s) => s.state === "current" || s.state === "blocked") || {}).key ?? null;
  return { stages, currentKey };
}

/** Approval-token status from its non-secret audit fields. */
export function tokenStatus(token, now = Date.now()) {
  if (token?.revoked) return "revoked";
  if (token?.used) return "used";
  const expires = asText(token?.expires_at);
  if (expires) {
    const parsed = parseUtc(expires);
    if (!Number.isNaN(parsed.getTime()) && parsed.getTime() <= now) return "expired";
  }
  return "pending";
}

/** The audited completion verdict of one experience-history entry. */
export function completionOf(entry) {
  const outcome = asText(entry?.outcome);
  const meta = COMPLETION_META[outcome];
  if (!meta) return null;
  const verification = entry?.verification && typeof entry.verification === "object" ? entry.verification : {};
  const basis = meta && verification.verified === true ? asText(verification.basis) : null;
  return {
    outcome,
    meta,
    verified: verification.verified === true,
    basis,
    basisLabel: basis ? VERIFICATION_BASIS_HE[basis] ?? basis : null,
    evidence: boundedText(verification.evidence, 240),
    verifiedAt: asText(verification.verified_at),
    summary: boundedText(entry?.summary, 240),
  };
}

// ─── Remote-execution states (EXECUTION-CLOSURE) ─────────────────────────────
//
// The operator-facing execution states are decoded strictly from evidence the page already
// receives: the workspace's remote_readiness contract (state / transports / per-tool entitlement
// / the standing approval class), the crisis read and the employee's own enabled flag, and each
// stored operational session's own execution rows (success, verification verdict, blocked reason,
// credential lifecycle booleans). Absent evidence reads as unknown — never as available, never as
// success, never as "nothing happened". The two boundary constants mirror the recorded backend
// values (browser_work_executor.WRITE_COMPENSATION_BOUNDARY / the live-write arm's recorded
// unavailability) and are rendered as limitations only — never as a capability or a switch.

const REASON_LIVE_WRITE_ARM_UNAVAILABLE = "live_browser_write_arm_unavailable";
const REASON_POSTCONDITION_MISMATCH = "postcondition_read_back_mismatch";
const VERIFICATION_POSTCONDITION_READ_BACK = "postcondition_read_back";
const VERIFICATION_PROVIDER_RUN = "provider_run_success";

export const WRITE_BOUNDARY_HE = "כתיבה מול יעד אמיתי: אינה זמינה בכל תצורה — מסלול הכתיבה החי לא קיים.";
export const ROLLBACK_BOUNDARY_HE = "שחזור אוטומטי: לא קיים — היפוך שינוי (אם יידרש) הוא ידני בלבד.";

/** Verdict of the workspace's remote_readiness contract. An unrecognized state stays unknown. */
export function remoteReadinessVerdict(readiness) {
  if (!readiness || typeof readiness !== "object" || Array.isArray(readiness)) return { state: null, reasons: [] };
  const state = asText(readiness.state);
  const reasons = (Array.isArray(readiness.reasons) ? readiness.reasons : [])
    .map((reason) => boundedText(reason, 160))
    .filter(Boolean);
  return { state: state === "ready" || state === "not_ready" ? state : null, reasons };
}

function readinessTransport(readiness, role) {
  const transports = Array.isArray(readiness?.transports) ? readiness.transports : [];
  return transports.find((entry) => entry && entry.role === role) ?? null;
}

/** The bounded read-only diagnostics arm (the named OpenClaw registry tools), or null when the
 * readiness block was not reported — never "available" without evidence. */
export function readArmState(readiness) {
  if (!readiness || typeof readiness !== "object") return null;
  const runtime = readinessTransport(readiness, "runtime_adapter");
  const entitled = (Array.isArray(readiness.actions) ? readiness.actions : []).filter((a) => a && a.entitled === true);
  if (!runtime || runtime.status !== "ok") {
    return { key: "no_runtime", label: "אין נתיב לאבחון מרחוק — סביבת ההרצה של OpenClaw אינה מוגדרת" };
  }
  if (entitled.length === 0) {
    return { key: "not_entitled", label: "אין הרשאה לאף אחת מפעולות האבחון המרחוקות המוגבלות-שם" };
  }
  const names = entitled.map((a) => boundedText(a.name, 40)).filter(Boolean).join(", ");
  return { key: "read_only", label: `זמין בקריאה בלבד${names ? ` — ${names}` : ""}; כל קריאה נרשמת כשורת ביקורת` };
}

/** The remote write arm. Never "available": the most permissive truthful state is the standing
 * human-approval gate; crisis, a disabled employee and a missing managed transport are hard
 * states read from evidence, and an unreported readiness block stays unknown. */
export function writeArmState(readiness, { crisis = null, agentEnabled = null } = {}) {
  if (crisis === true) {
    return { key: "blocked_crisis", label: "חסומה — מצב חירום פעיל: יצירת סשנים וכל פעולות הכתיבה מושהות" };
  }
  if (agentEnabled === false) {
    return { key: "employee_disabled", label: "לא זמינה — העובד מושבת; אין נתיב לפתיחת סשן או לביצוע פעולה" };
  }
  if (!readiness || typeof readiness !== "object") return null;
  const transport = readinessTransport(readiness, "selected_transport");
  if (!transport || transport.status !== "ok") {
    return { key: "no_transport", label: "לא זמינה — אמצעי החיבור המנוהל (TeamViewer) אינו מוגדר; אין נתיב לפתיחת סשן" };
  }
  return { key: "approval_gated", label: "מותנית באישור אנושי בלבד — אין מסלול אוטונומי לפתיחת סשן או לביצוע פעולה משנה-מצב" };
}

const SESSION_STAGE_HE = {
  requested: "נשלח",
  submitted: "נשלח",
  queued: "בתור",
  policy_check: "בדיקת מדיניות",
  working: "בעבודה",
  awaiting_approval: "ממתין לאישור",
  approved: "אושר — ממתין לביצוע",
  connecting: "מתחבר",
  running: "בעבודה",
  waiting_for_connection: "ממתין לחיבור הלקוח",
  waiting_for_customer: "ממתין ללקוח",
  waiting_for_user: "ממתין לפעולת משתמש",
  waiting_for_credential: "ממתין למסירת אישורי התחברות",
  handed_off: "הועבר לטכנאי",
  completed: "הושלם",
  verified: "אומת",
  failed: "נכשל",
  stopped: "נעצר",
  execution_blocked: "נחסמה",
};

const SESSION_WAITING_KINDS = {
  awaiting_approval: "approval",
  waiting_for_approval: "approval",
  pending_approval: "approval",
  waiting_for_user: "user",
  awaiting_user: "user",
  waiting_user: "user",
  waiting_for_connection: "connection",
  waiting_for_customer: "customer",
  waiting_for_credential: "credential",
  approval: "approval",
  user: "user",
  connection: "connection",
  customer: "customer",
  credential: "credential",
};

const SESSION_WAITING_HE = {
  approval: "ממתין לאישור אנושי",
  user: "ממתין לפעולת משתמש/מפעיל",
  connection: "ממתין לחיבור הלקוח",
  customer: "ממתין ללקוח",
  credential: "ממתין למסירת אישורי התחברות (מסירה ידנית בלבד)",
};

/** The session's own reported stage. An unrecognized stored value is surfaced bounded and marked
 * unknown — never mapped onto a known stage. */
export function sessionStageOf(session) {
  const raw = [session?.stage, session?.current_stage, session?.state, session?.status].map(asText).find(Boolean) ?? null;
  const key = raw ? raw.trim().toLowerCase().replace(/[\s-]+/g, "_") : null;
  return { key, label: key ? SESSION_STAGE_HE[key] ?? null : null, raw: boundedText(raw, 40) };
}

/** The session's wait kind and whether its stored wait window has expired. */
export function sessionWaitingOf(session) {
  const stageKey = sessionStageOf(session).key;
  const rawWaiting = asText(session?.waiting_for)?.trim().toLowerCase().replace(/[\s-]+/g, "_") ?? null;
  const kind = SESSION_WAITING_KINDS[stageKey] ?? SESSION_WAITING_KINDS[rawWaiting] ?? null;
  if (!kind) return null;
  const execution = session?.execution && typeof session.execution === "object" ? session.execution : {};
  return { kind, label: SESSION_WAITING_HE[kind], expired: execution.wait_expired === true };
}

/** One stored session's execution verdict, decoded strictly from its own execution rows. A
 * combination that cannot be grounded returns null — nothing is invented, and a reported success
 * without a reported verification basis is never rendered as verified. */
export function sessionExecutionVerdict(session) {
  const execution = session?.execution && typeof session.execution === "object" ? session.execution : {};
  const blockedReason = asText(execution.blocked_reason);
  if (blockedReason === REASON_LIVE_WRITE_ARM_UNAVAILABLE) {
    return {
      key: "write_blocked",
      icon: "⛔",
      title: "כתיבה חסומה",
      cls: "bg-red-50 text-red-800 border-red-200",
      note: "אין מסלול כתיבה חי — הפעולה נעצרה לפני כל מגע עם היעד; לא בוצע שינוי.",
    };
  }
  if (execution.success === true) {
    if (execution.verification === VERIFICATION_POSTCONDITION_READ_BACK) {
      return {
        key: "verified_write",
        icon: "✓",
        title: "פעולה משנה־מצב אומתה בקריאה חוזרת מהיעד",
        cls: "bg-green-50 text-green-800 border-green-200",
        note: ROLLBACK_BOUNDARY_HE,
      };
    }
    if (execution.verification === VERIFICATION_PROVIDER_RUN) {
      return {
        key: "verified_read",
        icon: "✓",
        title: "קריאה בלבד אומתה בהרצת הספק — לא בוצע שינוי ביעד",
        cls: "bg-green-50 text-green-800 border-green-200",
        note: null,
      };
    }
    return {
      key: "reported_unverified",
      icon: "◷",
      title: "הצלחה מדווחת — סוג האימות לא דווח; אינה מוצגת כמאומתת",
      cls: "bg-amber-50 text-amber-800 border-amber-200",
      note: `לא ניתן לקבוע אם בוצע שינוי ביעד. ${ROLLBACK_BOUNDARY_HE}`,
    };
  }
  if (execution.success === false) {
    if (blockedReason === REASON_POSTCONDITION_MISMATCH) {
      return {
        key: "verification_failed",
        icon: "✗",
        title: "האימות לא עבר — הקריאה החוזרת לא תאמה את הפעולה המבוקשת",
        cls: "bg-red-50 text-red-800 border-red-200",
        note: `ייתכן שהשינוי בוצע ואינו מאומת — יש לאמת ידנית מול הראיות. ${ROLLBACK_BOUNDARY_HE}`,
      };
    }
    return {
      key: "not_verified",
      icon: "✗",
      title: "הסתיים ללא אימות הצלחה — דבר אינו מדווח כמוצלח",
      cls: "bg-red-50 text-red-800 border-red-200",
      note: ROLLBACK_BOUNDARY_HE,
    };
  }
  return null;
}

/** Credential lifecycle of one session — booleans and a count only; no credential value can exist
 * on this projection, and nothing else from the execution block is read here. */
export function sessionCredentialLifecycle(session) {
  const execution = session?.execution && typeof session.execution === "object" ? session.execution : {};
  const credential = execution.credential && typeof execution.credential === "object" ? execution.credential : null;
  if (!credential) return [];
  return [
    credential.received === true ? "נמסרו לסשן" : credential.received === false ? "לא נמסרו" : null,
    credential.consumed === true ? "נצרכו פעם אחת" : credential.consumed === false ? "לא נצרכו" : null,
    Number.isInteger(credential.purged) ? `נמחקו מהמאגר הזמני: ${credential.purged}` : null,
  ].filter(Boolean);
}

/** The workspace's operational-session evidence. An absent source is unknown (never "none"). */
export function recentOperationalSessions(workspace) {
  const block = workspace?.browser_work_sessions;
  const sessions = block && typeof block === "object" && Array.isArray(block.sessions) ? block.sessions : null;
  return { known: sessions !== null, sessions: (sessions ?? []).filter((s) => s && typeof s === "object") };
}

// ─── Small presentational pieces ─────────────────────────────────────────────

const WRITE_ARM_CLS = {
  blocked_crisis: "text-red-700 font-medium",
  employee_disabled: "text-red-700 font-medium",
  no_transport: "text-red-700 font-medium",
  approval_gated: "text-amber-700 font-medium",
};

function Pill({ cls, children, title }) {
  return (
    <span title={title} className={`text-[11px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${cls}`}>
      {children}
    </span>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <section className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-gray-500 shrink-0 w-36">{label}</span>
      <span className="text-gray-800 min-w-0 flex-1">{children}</span>
    </div>
  );
}

function EmptyLine({ children }) {
  return <p className="text-xs text-gray-400 italic">{children}</p>;
}

// ─── Stage tracker ───────────────────────────────────────────────────────────

function StageTracker({ stages, currentKey }) {
  return (
    <section aria-label="שלבי הסשן" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-semibold text-gray-800">שלב נוכחי</h2>
        {currentKey && (
          <span className="text-xs text-gray-500">
            השלב הנוכחי: <span className="font-medium text-gray-800">{SESSION_STAGES.find((s) => s.key === currentKey)?.label}</span>
          </span>
        )}
      </div>
      <ol className="flex flex-wrap gap-2">
        {stages.map((stage) => {
          const meta = STAGE_STATE_META[stage.state];
          const isCurrent = stage.key === currentKey;
          return (
            <li
              key={stage.key}
              aria-current={isCurrent ? "step" : undefined}
              className={`flex-1 min-w-[8.5rem] rounded-lg border px-3 py-2 space-y-0.5 ${meta.cls}`}
            >
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <span aria-hidden="true">{meta.glyph}</span>
                <span>{stage.label}</span>
                <span className="sr-only">— {meta.label}</span>
              </div>
              <p className="text-[11px] opacity-80">{stage.note || stage.hint}</p>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// ─── Operational session evidence ────────────────────────────────────────────

function SessionEvidenceRow({ session }) {
  const stage = sessionStageOf(session);
  const waiting = sessionWaitingOf(session);
  const verdict = sessionExecutionVerdict(session);
  const credentialLifecycle = sessionCredentialLifecycle(session);
  const blockedRaw = boundedText(session.execution?.blocked_reason, 60);
  const ticket = session.ticket && typeof session.ticket === "object" ? session.ticket : null;
  const ticketId = ticket?.id;
  const ticketNumber = ticket?.ticket_number;
  const runId = asText(session.run_id);

  return (
    <li className="rounded-lg border border-gray-200 px-3 py-2 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {stage.label ? (
          <Pill cls="bg-gray-100 text-gray-700 border-gray-200">{stage.label}</Pill>
        ) : stage.raw ? (
          <Pill cls="bg-gray-100 text-gray-500 border-gray-200">{`שלב לא מזוהה: ${stage.raw}`}</Pill>
        ) : (
          <Pill cls="bg-gray-100 text-gray-400 border-gray-200">שלב לא דווח</Pill>
        )}
        {waiting && (
          <Pill cls={waiting.expired ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
            {waiting.expired ? `${waiting.label} — פג תוקף` : waiting.label}
          </Pill>
        )}
        {ticketNumber != null &&
          (ticketId ? (
            <Link to={`/tickets/${ticketId}`} className="text-blue-600 hover:underline">{`קריאה #${ticketNumber}`}</Link>
          ) : (
            <span className="text-gray-600">{`קריאה #${ticketNumber}`}</span>
          ))}
        {runId && (
          <Link to={`/agent-runs/${runId}`} className="text-blue-600 hover:underline mr-auto">לריצה</Link>
        )}
      </div>

      {verdict && (
        <div className={`rounded border px-2 py-1 text-[11px] space-y-0.5 ${verdict.cls}`}>
          <p className="font-medium">
            {`${verdict.icon} ${verdict.title}`}
          </p>
          {verdict.note && <p className="opacity-90">{verdict.note}</p>}
        </div>
      )}

      {blockedRaw && (
        <p className="text-[11px] text-gray-500">
          סיבת החסימה המדווחת: <span className="font-mono" dir="ltr">{blockedRaw}</span>
        </p>
      )}

      {credentialLifecycle.length > 0 && (
        <p className="text-[11px] text-gray-500">
          {`אישורי התחברות (מחזור חיים בלבד, ללא ערכים): ${credentialLifecycle.join(" · ")}`}
        </p>
      )}
    </li>
  );
}

function OperationalSessions({ known, sessions, shown }) {
  return (
    <section aria-label="סשנים תפעוליים אחרונים" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-gray-800">סשנים תפעוליים אחרונים</h2>
        <p className="text-xs text-gray-500 mt-0.5">ראיות הביצוע השמורות לכל סשן — שלב, המתנה, תוצאת ביצוע ואימות; תצוגה בלבד</p>
      </div>
      {!known ? (
        <EmptyLine>מקור הסשנים התפעוליים טרם נחשף בגרסת השרת הזו — לא ניתן לקבוע אם בוצעו סשנים לעובד זה.</EmptyLine>
      ) : sessions.length === 0 ? (
        <EmptyLine>לא נרשמו סשנים תפעוליים לעובד זה.</EmptyLine>
      ) : (
        <>
          <ul className="space-y-2">
            {shown.map((session, index) => (
              <SessionEvidenceRow key={session.id ?? index} session={session} />
            ))}
          </ul>
          {sessions.length > shown.length && (
            <p className="text-[11px] text-gray-500">{`מוצגים ${shown.length} מתוך ${sessions.length} סשנים.`}</p>
          )}
        </>
      )}
    </section>
  );
}

// ─── Remote connection-profile contract (PROD-REMOTECONTRACT-028) ────────────
//
// The §4.1/§4.2/§4.3 contract (FULL-0095 broker) made operator-reachable through the admin-gated
// routes on the agents router. The panel shows the active run's ticket/customer binding, the ONE
// profile scoped to that customer, §4.3's nine actions with that profile's read-only allowlist,
// and the §4.2 receipt the broker records for one selected action. Since FULL-0131 the profile can
// be a DURABLE customer profile (`profile_source === "stored"`) instead of the DEV fixture the
// surface shipped with; the badge and every label below follow that fact rather than assuming DEV.
// It offers no execution control: the receipt is a record and every answer says so.

export const RECEIPT_STATE_HE = {
  requested: "התבקשה",
  policy_check: "נבדקה במדיניות",
  awaiting_approval: "ממתינה לאישור אנושי",
  approved: "אושרה לרישום — רשומה בלבד",
  credential_issued: "אישור גישה הונפק (רשומה)",
  session_starting: "הסשן מתחיל (רשומה)",
  active: "פעיל (רשומה)",
  verifying: "מאמתת תנאי סיום",
  closed: "נסגרה ואומתה",
  failed: "נדחתה",
  revoked: "בוטלה",
  not_bound: "לא נכרכה לקריאה/לקוח",
};

export const REFUSAL_ARM_HE = {
  unknown_profile: "לא נמצא פרופיל חיבור ללקוח ולספק",
  ambiguous_profile: "יותר מפרופיל אחד מתאים — יש לאחד",
  profile_untested_for_write: "הפרופיל טרם נבדק — פעולה משנה-מצב חסומה",
  profile_degraded_for_write: "הפרופיל מדורג כפגום — פעולה משנה-מצב חסומה",
  profile_revoked: "הפרופיל בוטל",
  profile_expired: "תוקף הפרופיל פג",
  profile_unhealthy: "מצב החיבור נכשל בבדיקה",
  ticket_customer_mismatch: "הקריאה והלקוח אינם תואמים",
  tenant_mismatch: "הארגון אינו תואם לפרופיל",
  action_unknown: "הפעולה אינה מוצהרת בקטלוג",
  action_not_bound_to_registry: "אין כלי רשום שמבצע את הפעולה",
  employee_not_allowed: "העובד אינו ברשימת הפרופיל",
  target_not_allowed: "היעד אינו ברשימת הפרופיל",
  action_not_allowed: "הפעולה אינה ברשימת הפרופיל",
  operation_ceiling_exceeded: "הפעולה חורגת מתקרת סוג הפעולה",
  risk_ceiling_exceeded: "הפעולה חורגת מתקרת הסיכון",
  requester_role_not_allowed: "תפקיד המבקש נמוך מהנדרש",
  crisis_mode_write_blocked: "מצב חירום חוסם פעולה משנה-מצב",
  approval_missing: "חסר אישור אנושי תקף — אין מסלול אוטומטי",
  outside_time_window: "מחוץ לחלון הזמן של הפרופיל",
  network_not_allowed: "הרשת אינה מורשית בפרופיל",
};

export function refusalArmLabel(arm) {
  return REFUSAL_ARM_HE[arm] ?? arm;
}

export function actionAllowedByFixture(allowlist, name) {
  const actions = Array.isArray(allowlist?.actions) ? allowlist.actions : [];
  return actions.includes(name);
}

// What the profile actually is. A response without `profile_source` predates FULL-0131, when the
// only source this surface could return was the DEV fixture, so that fallback is truthful for the
// compatibility window rather than an optimistic default.
export const PROFILE_SOURCE_HE = {
  stored: "פרופיל שמור · הוגדר באישור דו-אנשי",
  dev_fixture: "DEV · נתוני חיבור לדוגמה",
  none: "לא נבחר פרופיל",
};

export function profileSourceLabel(source) {
  return PROFILE_SOURCE_HE[source] ?? `מקור פרופיל לא מוכר: ${source}`;
}

function RemoteConnectionContractPanel({ agentId, canManage }) {
  const [selectedAction, setSelectedAction] = useState("");
  const [requestedAction, setRequestedAction] = useState("");

  const contractQuery = useQuery({
    queryKey: ["remote-ops-connection-contract", agentId],
    queryFn: () => agentsApi.remoteConnectionProfiles(agentId),
    enabled: canManage && !!agentId,
    staleTime: 30_000,
  });
  const receiptQuery = useQuery({
    queryKey: ["remote-ops-session-receipt", agentId, requestedAction],
    queryFn: () => agentsApi.remoteSessionReceipt(agentId, { action: requestedAction }),
    enabled: canManage && !!agentId && !!requestedAction,
    retry: false,
  });

  if (!canManage || !agentId) return null;

  const contract = contractQuery.data ?? null;
  const binding = contract?.binding ?? null;
  const profiles = Array.isArray(contract?.profiles) ? contract.profiles : [];
  const profile = profiles[0] ?? null;
  const allowlist = contract?.fixture_allowlist ?? null;
  const actions = Array.isArray(contract?.actions) ? contract.actions : [];
  const receiptAnswer = receiptQuery.data ?? null;
  const receipt = receiptAnswer?.receipt ?? null;
  const profileSource = contract?.profile_source ?? "dev_fixture";
  const isStored = profileSource === "stored";
  const profileWord = isStored ? "בפרופיל השמור" : "בפרופיל ה-DEV";
  const revoked = Boolean(profile?.revoked_at || profile?.state === "revoked");

  return (
    <section
      data-testid="remote-connection-contract"
      aria-label="חוזה פרופיל חיבור מרחוק"
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
    >
      <div>
        <h2 className="text-sm font-semibold text-gray-800">חוזה פרופיל חיבור מרחוק</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          פרופיל החיבור התחום-לקוח, קטלוג הפעולות המרחוקות וקבלת הסשן שהמנהלן רושם — רשומה בלבד;
          אינו מבצע פעולה, אינו פותח סשן ואינו משנה שערי אישור.
        </p>
      </div>

      {contractQuery.isLoading ? (
        <p className="text-xs text-gray-500">טוען את חוזה פרופיל החיבור…</p>
      ) : contractQuery.isError ? (
        <div role="alert" className="rounded border border-gray-300 bg-gray-50 text-gray-700 text-xs px-3 py-2 space-y-1">
          <p>
            {contractQuery.error?.response?.status === 403
              ? "נדרשת הרשאת מנהל או בעלים כדי לקרוא את חוזה פרופיל החיבור."
              : "לא ניתן לקרוא את חוזה פרופיל החיבור — המצב אינו ידוע."}
          </p>
          <button type="button" onClick={() => contractQuery.refetch()} className="underline">נסה שוב</button>
        </div>
      ) : contract ? (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Pill cls={
              profileSource === "stored"
                ? "bg-sky-50 text-sky-800 border-sky-300"
                : profileSource === "dev_fixture"
                  ? "bg-amber-50 text-amber-800 border-amber-300"
                  : "bg-gray-50 text-gray-600 border-gray-300"
            }>
              {profileSourceLabel(profileSource)}
            </Pill>
            <span className="text-gray-500">
              {profileSource === "stored"
                ? "החיבור טרם נבדק; אין פתרון סוד ואין ביצוע."
                : profileSource === "dev_fixture"
                  ? "אין חיבור אמיתי, אין סוד ואין ביצוע."
                  : "אין פרופיל תחום ללקוח של הריצה."}
            </span>
          </div>

          <div className="space-y-1.5">
            <Row label="ריצה פעילה">
              {binding?.has_active_run ? (binding.run_id ?? "—") : "אין ריצה פעילה"}
            </Row>
            <Row label="קריאה">
              {binding?.ticket_number != null ? `#${binding.ticket_number}` : "—"}
            </Row>
            <Row label="לקוח">
              {binding?.customer_name || "—"}
            </Row>
            <Row label="עובד דיגיטלי">
              {binding?.employee_name || "—"}
            </Row>
            <Row label="כריכה">
              {binding?.bound ? (
                <span className="text-green-700 font-medium">מלאה — קריאה, לקוח ועובד נגזרו מהריצה</span>
              ) : (
                <span className="text-amber-700">{binding?.detail ?? "לא נכרך"}</span>
              )}
            </Row>
          </div>

          {profile ? (
            <div className="border-t pt-2 space-y-1.5">
              <p className="text-[11px] text-gray-400">פרופיל החיבור שנבחר (תחום ללקוח של הריצה):</p>
              <Row label="ספק">{profile.provider}</Row>
              <Row label={isStored ? "ארגון" : "ארגון (DEV)"}>{profile.tenant_id}</Row>
              <Row label="מקור הפרופיל">
                {isStored ? "פרופיל לקוח שמור" : "נתוני DEV — אין פרופיל שמור ללקוח"}
              </Row>
              <Row label="מצב הפרופיל">
                {revoked ? (
                  <span className="text-red-700 font-medium">בוטל — אינו משרת פעולה</span>
                ) : (
                  <span className="text-gray-700">פעיל (קריאה בלבד)</span>
                )}
              </Row>
              <Row label="מצב בדיקה">
                <span className="text-amber-700 font-medium">
                  {profile.health === "untested" ? "טרם נבדק — פעולה משנה-מצב חסומה" : profile.health}
                </span>
              </Row>
              <Row label={isStored ? "יעד" : "יעד (DEV)"}>{contract?.target ?? "—"}</Row>
              <Row label="פעולות מותרות">
                {Array.isArray(allowlist?.actions) && allowlist.actions.length > 0
                  ? allowlist.actions.join(", ")
                  : "—"}
              </Row>
              <Row label="תקרות">
                {allowlist
                  ? `סוג: ${operationTypeLabel(allowlist.operation_ceiling)} · סיכון: ${riskGradeLabel(allowlist.risk_ceiling)}`
                  : "—"}
              </Row>
              <p className="text-[11px] text-gray-400">
                הפניה לסוד קיימת בלבד ואינה נשלפת:{" "}
                {profile.credential?.scheme ?? "—"} · ערך גולמי: לא
              </p>
            </div>
          ) : (
            <EmptyLine>לא נבחר פרופיל חיבור — אין כריכת קריאה/לקוח מלאה.</EmptyLine>
          )}

          {actions.length > 0 && (
            <div className="border-t pt-2 space-y-1.5">
              <p className="text-[11px] text-gray-400">קטלוג הפעולות המרחוקות (§4.3):</p>
              <ul className="space-y-1">
                {actions.map((action) => {
                  const allowed = actionAllowedByFixture(allowlist, action.name);
                  return (
                    <li key={action.name} className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-mono text-gray-700" dir="ltr">{action.name}</span>
                      <Pill cls={action.read_only
                        ? "bg-gray-100 text-gray-600 border-gray-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"}>
                        {action.read_only ? "קריאה בלבד" : "משנה מצב"}
                      </Pill>
                      <Pill cls={allowed
                        ? "bg-green-50 text-green-700 border-green-200"
                        : "bg-red-50 text-red-700 border-red-200"}>
                        {allowed ? `מותר ${profileWord}` : `חסום ${profileWord}`}
                      </Pill>
                      {!action.bound && (
                        <span className="text-[11px] text-gray-400">מוצהרת בלבד — אין כלי מבצע</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {profile && actions.length > 0 && (
            <div className="border-t pt-2 space-y-2">
            <label htmlFor="remote-contract-action" className="block text-[11px] text-gray-500">
              בחר פעולה להפקת קבלת סשן (רשומה בלבד)
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <select
                id="remote-contract-action"
                value={selectedAction}
                onChange={(event) => setSelectedAction(event.target.value)}
                className="text-xs border rounded px-2 py-1 bg-white"
              >
                <option value="">— בחר פעולה —</option>
                {actions.map((action) => (
                  <option key={action.name} value={action.name}>{action.name}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={!selectedAction || receiptQuery.isFetching}
                onClick={() => setRequestedAction(selectedAction)}
                className="text-xs px-3 py-1 rounded border border-blue-300 bg-blue-50 text-blue-700 disabled:opacity-50"
              >
                {receiptQuery.isFetching ? "מפיק קבלה…" : "הפקת קבלת סשן"}
              </button>
            </div>
            <p className="text-[11px] text-gray-400">
              הקבלה נרשמת מהריצה הפעילה בלבד; השרת אינו מקבל לקוח, קריאה, יעד או אישור מהדפדפן.
            </p>

            {receiptQuery.isError ? (
              <p className="text-xs text-gray-500">
                לא ניתן להפיק את הקבלה — המצב אינו ידוע; לא בוצעה פעולה.
              </p>
            ) : receiptAnswer ? (
              <div data-testid="remote-session-receipt" className="rounded border border-gray-200 bg-gray-50 p-2 space-y-1.5 text-xs">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-800">מצב הקבלה:</span>
                  <span className={receiptAnswer.state === "approved" ? "text-green-700" : "text-red-700"}>
                    {RECEIPT_STATE_HE[receiptAnswer.state] ?? receiptAnswer.state}
                  </span>
                </div>
                <p className="text-gray-700">
                  {receiptAnswer.audit?.admitted
                    ? "הבקשה נרשמה כקבלה מאושרת לשלב הרשום — לא בוצעה פעולה מרחוק."
                    : "הבקשה נדחתה ולא נרשם סשן — לא בוצעה פעולה מרחוק."}
                </p>
                <p className="text-[11px] text-gray-500">
                  {receiptAnswer.profile_source === "stored"
                    ? "הקבלה הופקה מפרופיל לקוח שמור."
                    : receiptAnswer.profile_source === "dev_fixture"
                      ? "הקבלה הופקה מפרופיל DEV."
                      : "לא היה פרופיל תחום ללקוח."}
                </p>
                {receipt && Array.isArray(receipt.refusals) && receipt.refusals.length > 0 && (
                  <ul className="list-disc pr-5 space-y-0.5 text-gray-700">
                    {receipt.refusals.map((refusal, index) => (
                      <li key={`${refusal.arm}-${index}`}>
                        {refusalArmLabel(refusal.arm)}
                        <span className="text-gray-400 font-mono" dir="ltr"> ({refusal.arm})</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-gray-500" dir="ltr">
                  {`action: ${receipt?.request?.action ?? requestedAction} · authorizes_execution: ${String(receiptAnswer.audit?.authorizes_execution)} · external_effect: ${String(receiptAnswer.audit?.external_effect)}`}
                </p>
              </div>
            ) : null}
            </div>
          )}
        </>
      ) : (
        <EmptyLine>אין נתוני חוזה פרופיל חיבור להצגה.</EmptyLine>
      )}
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function RemoteOperationsPage() {
  const { can, loading: permsLoading } = useAgentPermissions();
  const canView = can("agents_view");
  const canManage = can("agents_manage");
  const isOnline = useOnlineStatus();
  const [selectedId, setSelectedId] = useState(null);
  // Recomputed every render so an expiring session stops reading as live without a remount;
  // all queries on this page refetch on an interval.
  const now = Date.now();

  const agentsQuery = useQuery({
    queryKey: ["remote-ops-agents"],
    queryFn: () => agentsApi.list(),
    enabled: canView,
    staleTime: 30_000,
  });
  const agents = agentsQuery.data ?? [];
  const activeId = selectedId ?? agents[0]?.id ?? null;
  const employee = agents.find((a) => a.id === activeId) ?? null;

  const workspaceQuery = useQuery({
    queryKey: ["remote-ops-workspace", activeId],
    queryFn: () => agentsApi.workspace(activeId),
    enabled: canView && !!activeId,
    refetchInterval: 30_000,
  });
  const workspace = workspaceQuery.data ?? null;

  const activeRunId = workspace?.current_work?.run_id ?? null;
  const runQuery = useQuery({
    queryKey: ["remote-ops-run", activeRunId],
    queryFn: () => agentRunsApi.get(activeRunId),
    enabled: canView && !!activeRunId,
    refetchInterval: 30_000,
  });
  const run = runQuery.data ?? null;

  const historyQuery = useQuery({
    queryKey: ["remote-ops-history", activeId],
    queryFn: () => agentRunsApi.listByAgent(activeId, { limit: 20 }),
    enabled: canView && !!activeId,
    staleTime: 30_000,
  });
  const runs = historyQuery.data ?? [];
  const terminalRuns = runs.filter((r) => TERMINAL_RUN_STATUSES.has(r.status));

  const tokensQuery = useQuery({
    queryKey: ["remote-ops-tokens", run?.id],
    queryFn: () => agentRunsApi.approvalTokens(run.id),
    enabled: canView && !!run?.id,
    staleTime: 15_000,
  });

  const experienceQuery = useQuery({
    queryKey: ["remote-ops-experience", activeId],
    queryFn: () => agentsApi.experienceHistory(activeId, { limit: 5 }),
    enabled: canView && !!activeId,
    staleTime: 30_000,
  });

  // Crisis mode gates every remote-session write; without agents_manage the state is unreadable
  // and is reported as unknown — never as "off".
  const crisisQuery = useQuery({
    queryKey: ["remote-ops-crisis"],
    queryFn: () => agentsApi.getCrisisMode(),
    enabled: canView && canManage,
    staleTime: 30_000,
  });
  const crisisKnown = canManage && !crisisQuery.isError && crisisQuery.data != null;
  const crisis = crisisKnown ? crisisQuery.data?.active === true : null;

  const healthQuery = useQuery({
    queryKey: ["connectionsHealth"],
    queryFn: connectionsApi.health,
    enabled: canView,
    staleTime: 60_000,
  });

  if (permsLoading) {
    return <div className="p-6 text-gray-500">טוען הרשאות…</div>;
  }

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות בפעולות מרחוק</div>;
  }

  const facts = readRunFacts(run);
  const { stages, currentKey } = buildSessionStages(run, now);
  const liveState = liveStateOf(facts, now);

  // Shared source-state ladder (statusVocabulary.classifySource). With an active run id known from
  // the workspace, a run fetch that never landed is unknown and one whose refresh failed while its
  // payload is retained is stale — neither is "no active run".
  const runState = classifySource(runQuery);
  const runPending = !!activeRunId && runState.key === SOURCE_STATES.loading;
  const runUnknown = !!activeRunId && runState.key === SOURCE_STATES.failed;
  const runStale = runState.key === SOURCE_STATES.stale;
  const runLoadedAt = runQuery.dataUpdatedAt > 0 ? new Date(runQuery.dataUpdatedAt).toLocaleTimeString("he-IL") : null;
  const workspaceState = classifySource(workspaceQuery);
  // Header truth for the selected employee's workspace feed (30s poller). A missing workspace
  // read is UNKNOWN/UNAVAILABLE, never a live claim; offline held data is CACHED.
  const workspaceTruth = classifySourceTruth(workspaceQuery, {
    online: isOnline,
    pollingMs: 30_000,
    enabled: canView && !!activeId,
  });
  const workspaceFailed = workspaceState.key === SOURCE_STATES.failed;
  const workspaceStale = workspaceState.key === SOURCE_STATES.stale;
  const workspaceLoadedAt = workspaceQuery.dataUpdatedAt > 0 ? new Date(workspaceQuery.dataUpdatedAt).toLocaleTimeString("he-IL") : null;
  const noRunText = (suffix) =>
    runUnknown
      ? "מצב הריצה אינו ידוע — טעינת פרטי הריצה נכשלה"
      : runPending
      ? "טוען את מצב הריצה…"
      : `אין ריצה פעילה — ${suffix}`;

  const connections = workspace?.capabilities?.connections ?? [];
  const entitledTools = workspace?.capabilities?.tools ?? [];
  const approvalTools = entitledTools.filter((t) => t.approval_required || t.strong_approval_required);
  const tokens = Array.isArray(tokensQuery.data) ? tokensQuery.data : [];
  const runActions = Array.isArray(run?.actions)
    ? [...run.actions].sort((a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0))
    : [];
  const shownActions = runActions.slice(0, 6);
  const tier = workspace?.agent?.permission_tier;
  const tierMeta = TIER_HE[tier] ?? null;
  const autonomy = employee?.autonomy_level;
  const experienceEntries = experienceQuery.data?.entries ?? [];
  const latestCompletion = experienceEntries.length > 0 ? completionOf(experienceEntries[0]) : null;
  const experienceSummary = experienceQuery.data?.summary ?? null;

  // Execution-closure states, decided only from evidence the page already receives.
  const readiness = workspace?.remote_readiness ?? null;
  const readinessVerdict = remoteReadinessVerdict(readiness);
  const readArm = readArmState(readiness);
  const writeArm = writeArmState(readiness, { crisis, agentEnabled: employee?.is_enabled ?? null });
  const approvalGate = readiness && typeof readiness.approval === "object" ? readiness.approval : null;
  const approvalGateLabel = approvalGate ? boundedText(approvalGate.label, 90) ?? "אישור אנושי נדרש" : null;
  const opSessions = recentOperationalSessions(workspace);
  const shownSessions = opSessions.sessions.slice(0, 3);

  const headlineStatus = run ? RUN_STATUS_HE[facts?.status] ?? facts?.status : null;

  return (
    <div className="space-y-5 max-w-6xl mx-auto" dir="rtl">
      <header className="space-y-1">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-800">פעולות מרחוק</h1>
          <SourceTruthBadge truth={workspaceTruth} testId="remote-ops-source-truth" />
        </div>
        <p className="text-sm text-gray-500 mt-0.5">
          יעד מדויק, חיבור, אישור נדרש, מצב מותר, שלב נוכחי, ראיות ומצב השלמה מאומת — לפי עובד דיגיטלי.
          תצוגת פיקוח בלבד; הפעלת סשן, אישור פעולה וסימון חיבור מתבצעים במסכי הריצה שבהם חלות הרשאות
          האישור הקיימות.
        </p>
      </header>

      {!isOnline && (
        <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3">
          {workspaceQuery.data != null
            ? "אין חיבור לרשת — נתוני הפעולות מרחוק לא מתרעננים כרגע; מוצג הנתון מהטעינה האחרונה שהצליחה."
            : "אין חיבור לרשת — לא ניתן לטעון את נתוני הפעולות מרחוק כרגע."}
        </p>
      )}

      {canManage && crisisQuery.isError && !crisisQuery.data && (
        <div className="rounded-lg border border-gray-300 bg-gray-50 text-gray-700 text-sm p-3">
          לא ניתן לקרוא את מצב החירום — המצב אינו ידוע.
        </div>
      )}
      {crisis === true && (
        <div className="rounded-lg border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm">
          <span className="font-semibold">מצב חירום פעיל</span> — כל פעולות הכתיבה ופתיחת הסשנים מרחוק חסומות.
        </div>
      )}

      {/* Employee selector */}
      <div className="bg-white rounded-xl border shadow-sm p-3">
        {agentsQuery.isLoading && <p className="text-sm text-gray-400 px-1 py-2">טוען עובדים...</p>}
        {agentsQuery.isError && (
          <p className="text-sm text-red-600 px-1 py-2">לא ניתן לטעון את רשימת העובדים — המצב אינו ידוע.</p>
        )}
        {!agentsQuery.isLoading && !agentsQuery.isError && agents.length === 0 && (
          <p className="text-sm text-gray-400 px-1 py-2">אין עובדים דיגיטליים להצגה</p>
        )}
        <div className="flex flex-wrap gap-2" role="group" aria-label="בחירת עובד דיגיטלי">
          {agents.map((a) => {
            const selected = a.id === activeId;
            // A disabled employee is offline whatever self-status is stored, so this cannot print
            // "זמין" on the button that prints " · מושבת" beside it.
            const statusKey = drawnStatus(a.availability_status, a.is_enabled);
            const statusMeta = STATUS_META[statusKey] ?? null;
            return (
              <button
                key={a.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setSelectedId(a.id)}
                className={`flex items-center gap-2 text-right px-3 py-2 rounded-lg border transition-colors ${
                  selected ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <EmployeeAvatar name={a.name} avatarKey={a.avatar_key} avatarColor={a.avatar_color} size="sm" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-800 truncate">
                    {a.name}
                    {a.is_enabled === false && <span className="text-[11px] text-gray-400 font-normal"> · מושבת</span>}
                  </span>
                  <span className="block text-[11px] text-gray-500 truncate">
                    {[statusMeta?.label ?? statusKey, a.pending_runs_count > 0 ? `${a.pending_runs_count} ממתינות` : null]
                      .filter(Boolean).join(" · ")}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {activeId && workspaceQuery.isLoading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {activeId && workspaceFailed && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
          לא ניתן לטעון את נתוני העובד.
          <button type="button" onClick={() => workspaceQuery.refetch()} className="mr-2 underline">נסה שוב</button>
        </div>
      )}

      {activeId && workspaceStale && (
        <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3">
          <span>
            רענון נתוני העובד נכשל — מוצג הנתון מהטעינה האחרונה שהצליחה
            {workspaceLoadedAt ? ` (${workspaceLoadedAt})` : ""}; ייתכן שהמצב אינו מעודכן.
          </span>
          <button type="button" onClick={() => workspaceQuery.refetch()} className="mr-2 underline">נסה שוב</button>
        </div>
      )}

      {workspace && (
        <>
          {/* Session headline */}
          <section className="bg-white rounded-xl border shadow-sm p-4 space-y-2" aria-label="מצב סשן מרחוק">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-gray-800">{workspace.agent?.name ?? employee?.name}</span>
              {run ? (
                <>
                  <Pill cls="bg-gray-100 text-gray-700 border-gray-200">{headlineStatus}</Pill>
                  {run.ticket_number != null && (
                    <span className="text-xs text-gray-500">{`קריאה #${run.ticket_number}${run.ticket_title ? ` · ${run.ticket_title}` : ""}`}</span>
                  )}
                </>
              ) : runUnknown ? (
                <>
                  <span className={`${CHIP_CLS} ${statusChipMeta("unknown").cls}`}>{statusChipMeta("unknown").label}</span>
                  <span className="text-xs text-gray-600">לא ניתן לטעון את מצב הריצה — טעינת פרטי הריצה נכשלה.</span>
                  <button type="button" onClick={() => runQuery.refetch()} className="text-xs text-blue-600 underline">נסה שוב</button>
                </>
              ) : runPending ? (
                <span className={`${CHIP_CLS} ${statusChipMeta("loading").cls}`}>{statusChipMeta("loading").label}</span>
              ) : (
                <Pill cls="bg-gray-100 text-gray-500 border-gray-200">אין ריצה פעילה</Pill>
              )}
              {run?.id && (
                <Link to={`/agent-runs/${run.id}`} className="text-xs text-blue-600 hover:underline mr-auto">
                  למסך הריצה
                </Link>
              )}
            </div>
            {liveState.live && (
              <p className="text-xs text-green-700">
                סשן מרחוק בתוקף
                {facts?.session?.validUntil ? ` · בתוקף עד ${fmtDateTime(facts.session.validUntil) ?? facts.session.validUntil}` : ""}
              </p>
            )}
            {runStale && (
              <p role="status" className="text-xs text-amber-700">
                רענון פרטי הריצה נכשל — מוצג הנתון מהטעינה האחרונה שהצליחה
                {runLoadedAt ? ` (${runLoadedAt})` : ""}; ייתכן שמצב הסשן אינו מעודכן.
              </p>
            )}
          </section>

          {runUnknown || runPending ? (
            <section aria-label="שלבי הסשן" className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
              <h2 className="text-sm font-semibold text-gray-800">שלב נוכחי</h2>
              <p role="status" className="text-sm text-gray-600">
                {runUnknown ? "שלבי הסשן אינם ידועים — טעינת פרטי הריצה נכשלה." : "טוען את שלבי הסשן…"}
              </p>
            </section>
          ) : (
            <StageTracker stages={stages} currentKey={currentKey} />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Target */}
            <Card title="היעד המדויק" subtitle="הקריאה, הלקוח ומזהה החיבור שסופק מפורשות">
              {run ? (
                <div className="space-y-2">
                  <Row label="קריאה">
                    {run.ticket_number != null
                      ? `#${run.ticket_number}${run.ticket_title ? ` · ${run.ticket_title}` : ""}`
                      : "—"}
                  </Row>
                  <Row label="לקוח">{run.ticket_company_name || "—"}</Row>
                  <Row label="מזהה TeamViewer">
                    {facts?.teamviewerId ? (
                      <span className="font-mono">{facts.teamviewerId}</span>
                    ) : (
                      <span className="text-amber-700">לא סופק מזהה מפורשות</span>
                    )}
                  </Row>
                  {!facts?.teamviewerId && facts?.lookupStatus && (
                    <p className="text-[11px] text-amber-700">{LOOKUP_STATUS_HE[facts.lookupStatus] ?? "איתור המכשיר לא סופק"}</p>
                  )}
                  <p className="text-[11px] text-gray-400">
                    לפי המדיניות יעד החיבור חייב להיות מסופק מפורשות על-ידי אדם; המערכת אינה בוחרת יעד באופן עצמאי ממלאי המכשירים.
                  </p>
                  <div className="flex gap-3 text-xs">
                    {run.ticket_id && (
                      <Link to={`/tickets/${run.ticket_id}`} className="text-blue-600 hover:underline">לכרטיס הקריאה</Link>
                    )}
                    <Link to={`/agent-runs/${run.id}`} className="text-blue-600 hover:underline">לריצה</Link>
                    {facts?.conversationId && (
                      <Link to={`/conversations/${facts.conversationId}`} className="text-blue-600 hover:underline">לשיחה</Link>
                    )}
                  </div>
                </div>
              ) : (
                <EmptyLine>{noRunText("אין יעד לסשן מרחוק כרגע")}</EmptyLine>
              )}
            </Card>

            {/* Connection */}
            <Card title="החיבור" subtitle="תעבורה מוגדרת ומצב הסשן בסבב הנוכחי">
              {healthQuery.isError ? (
                <EmptyLine>מצב התעבורה אינו ידוע — טעינת מצב החיבורים נכשלה</EmptyLine>
              ) : (
                <div className="space-y-2">
                  <Row label="TeamViewer">
                    {healthQuery.isLoading ? "טוען..." : healthQuery.data?.teamviewer?.configured
                      ? <Pill cls="bg-green-100 text-green-700 border-green-200">מוגדר</Pill>
                      : <Pill cls="bg-red-100 text-red-700 border-red-200">לא מוגדר</Pill>}
                  </Row>
                  <Row label="OpenClaw">
                    {healthQuery.isLoading ? "טוען..." : healthQuery.data?.openclaw?.configured
                      ? <Pill cls="bg-green-100 text-green-700 border-green-200">מוגדר</Pill>
                      : <Pill cls="bg-red-100 text-red-700 border-red-200">לא מוגדר</Pill>}
                    {healthQuery.data?.openclaw?.configured && healthQuery.data?.openclaw?.mode_label && (
                      <span className="text-[11px] text-gray-500 mr-2">{healthQuery.data.openclaw.mode_label}</span>
                    )}
                  </Row>
                </div>
              )}
              <div className="border-t pt-2 space-y-1">
                <p className="text-[11px] text-gray-400">מצב הסשן (ראיות מהריצה הנוכחית):</p>
                {!run ? (
                  <EmptyLine>{noRunText("אין סשן מרחוק")}</EmptyLine>
                ) : liveState.live ? (
                  <>
                    <Row label="מצב">
                      <span className={`${CHIP_CLS} ${LIVE_STATUS_CLS.active}`}>{statusChipMeta("active").label}</span>
                      <span className="mr-2 text-xs text-green-700">
                        סשן בתוקף — ממתין לחיבור הלקוח; עם החיבור המסלול הוא קריאה בלבד — כל פעולה משנה-מצב מחייבת אישור אנושי מראש.
                      </span>
                    </Row>
                    {facts?.session?.validUntil && (
                      <Row label="בתוקף עד">{fmtDateTime(facts.session.validUntil) ?? facts.session.validUntil}</Row>
                    )}
                  </>
                ) : liveState.reason === "crisis" ? (
                  <Row label="מצב">
                    <span className={`${CHIP_CLS} ${LIVE_STATUS_CLS.blocked}`}>{statusChipMeta("blocked").label}</span>
                    <span className="mr-2 text-xs text-red-700">אין מסלול חיבור מרחוק — מצב חירום חוסם פתיחת סשן</span>
                  </Row>
                ) : liveState.reason === "expired" ? (
                  <p className="text-xs text-amber-700">
                    סשן קודם פג תוקף — יש להכין סשן חדש לפני חיבור. פרטי הגישה פגו עם הסשן (מונפקים לסשן בלבד) — נדרשת הנפקה מחדש דרך מסלול האישור.
                  </p>
                ) : liveState.reason === "stale" ? (
                  <p className="text-xs text-amber-700">סשן מתועד שאינו תקף לריצה זו — יש להכין סשן חדש לפני חיבור.</p>
                ) : liveState.reason === "creation_failed" ? (
                  <p className="text-xs text-amber-700">יצירת הסשן נכשלה — ניתן להמשיך במסלול מזהה וסיסמה ידניים.</p>
                ) : facts?.connectionState === "active" ? (
                  <p className="text-xs text-green-700">חיבור פעיל מול הלקוח — מסלול קריאה בלבד; פעולות משנה-מצב מתבצעות רק באישור אנושי מראש.</p>
                ) : facts?.connectionState === "requested" ? (
                  <p className="text-xs text-gray-600">התבקש חיבור — ממתין לחיבור הלקוח.</p>
                ) : (
                  <EmptyLine>לא תועדו ראיות סשן לריצה זו</EmptyLine>
                )}
                <p className="text-[11px] text-gray-400">
                  קישור החיבור ללקוח ופרטי הגישה מנוהלים במסך הריצה בלבד; הראיות השמורות אינן כוללות סיסמאות, מפתחות או טוקנים.
                </p>
              </div>
            </Card>

            {/* Approval requirement */}
            <Card title="אישור נדרש" subtitle="מה מחייב אישור אנושי לפני ביצוע, ומה תועד בפועל">
              <div className="space-y-2">
                <Row label="בסיס מדיניות">
                  חיבור מרחוק ללקוח הוא סשן attended, וכל פעולה משנה-מצב מחייבת אישור אנושי מפורש לפני ביצוע.
                </Row>
                <Row label="שער האישור של העובד">
                  {approvalGateLabel ? (
                    <span className="text-amber-700 font-medium">
                      {`${approvalGateLabel} — אין מסלול אוטונומי לפתיחת סשן או לפעולה משנה-מצב`}
                    </span>
                  ) : (
                    <span className="text-gray-500">לא דווח על-ידי השרת — שער האישור אינו ידוע</span>
                  )}
                </Row>
                <Row label="הריצה הנוכחית">
                  {!run ? (
                    runUnknown ? "מצב הריצה אינו ידוע" : "—"
                  ) : facts?.status === "awaiting_technician_review" ? (
                    <span className="text-amber-700 font-medium">ממתינה לסקירת טכנאי</span>
                  ) : facts?.status === "awaiting_approval" ? (
                    <span className="text-amber-700 font-medium">ממתינה לאישור אנושי</span>
                  ) : (
                    RUN_STATUS_HE[facts?.status] ?? "—"
                  )}
                </Row>
              </div>
              <div className="border-t pt-2 space-y-1.5">
                <p className="text-[11px] text-gray-400">אישורים שהונפקו לריצה זו (יומן ביקורת):</p>
                {!run ? (
                  <EmptyLine>{noRunText("אין אישורים לתצוגה")}</EmptyLine>
                ) : tokensQuery.isError ? (
                  <p className="text-xs text-gray-500">ייתכן שיש אישורים שאינם מוצגים — קריאת יומן האישורים נכשלה.</p>
                ) : tokens.length === 0 ? (
                  <EmptyLine>לא הונפקו אישורים לריצה זו</EmptyLine>
                ) : (
                  tokens.map((token) => {
                    const status = tokenStatus(token, now);
                    const meta = APPROVAL_TOKEN_STATUS[status];
                    return (
                      <div key={token.id} className="flex flex-wrap items-center gap-2 text-xs">
                        <Pill cls={meta.cls}>{meta.label}</Pill>
                        <span className="font-medium text-gray-700">
                          {ACTION_TYPE_LABEL[token.action_type] ?? boundedText(token.action_type, 40) ?? "פעולה"}
                        </span>
                        {token.target && <span className="text-gray-500">{boundedText(token.target, 60)}</span>}
                        {Number.isInteger(token.risk_level) && (
                          <span className="text-[11px] text-gray-400">{riskGradeLabel(token.risk_level)}</span>
                        )}
                        {token.approved_at && (
                          <span className="text-[11px] text-gray-400 mr-auto">אושר: {fmtDateTime(token.approved_at) ?? "—"}</span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <div className="border-t pt-2 space-y-1.5">
                <p className="text-[11px] text-gray-400">כלים מורשים הדורשים אישור:</p>
                {entitledTools.length === 0 ? (
                  <EmptyLine>אין כלים מורשים לעובד — אין דרישות אישור</EmptyLine>
                ) : approvalTools.length === 0 ? (
                  <EmptyLine>אף כלי מורשה של העובד אינו דורש אישור</EmptyLine>
                ) : (
                  approvalTools.map((t) => (
                    <div key={t.name} className="flex items-center gap-2 text-xs">
                      <Pill cls={t.strong_approval_required
                        ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"}>
                        {t.strong_approval_required ? "אישור מוגבר" : "אישור נדרש"}
                      </Pill>
                      <span className="font-mono text-gray-700">{t.name}</span>
                      {t.operation_type && (
                        <span className="text-[11px] text-gray-400">{operationTypeLabel(t.operation_type)}</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Allowed mode */}
            <Card title="מצב מותר" subtitle="מה שהעובד רשאי לבצע כרגע, ומה חוסם">
              <div className="space-y-2">
                <Row label="מוכנות כללית">
                  {readinessVerdict.state === "ready" ? (
                    <span className="text-green-700 font-medium">מוכן לפעולות מרחוק — בכפוף לאישור האנושי החלה</span>
                  ) : readinessVerdict.state === "not_ready" ? (
                    <span className="text-red-700 font-medium">לא מוכן כרגע — חסימות מדווחות</span>
                  ) : (
                    <span className="text-gray-500">לא דווח על-ידי השרת — מצב המוכנות אינו ידוע</span>
                  )}
                </Row>
                {readinessVerdict.reasons.length > 0 && (
                  <ul className="list-disc pr-8 space-y-0.5 text-[11px] text-gray-600">
                    {readinessVerdict.reasons.map((reason, index) => (
                      <li key={`${index}-${reason}`}>{reason}</li>
                    ))}
                  </ul>
                )}
                <Row label="אבחון מרחוק (קריאה בלבד)">
                  {readArm ? (
                    <span className={readArm.key === "read_only" ? "text-gray-800" : "text-amber-700 font-medium"}>{readArm.label}</span>
                  ) : (
                    <span className="text-gray-500">לא דווח על-ידי השרת — נתיב האבחון מרחוק אינו ידוע</span>
                  )}
                </Row>
                <Row label="כתיבה מרחוק">
                  {writeArm ? (
                    <span className={WRITE_ARM_CLS[writeArm.key] ?? "text-gray-800"}>{writeArm.label}</span>
                  ) : (
                    <span className="text-gray-500">לא דווח על-ידי השרת — מצב נתיב הכתיבה אינו ידוע</span>
                  )}
                </Row>
                <Row label="שחזור (rollback)">
                  <span className="text-gray-700">{ROLLBACK_BOUNDARY_HE}</span>
                </Row>
                <Row label="הרשאת עובד">
                  {tierMeta ? `${tierMeta.label} — ${tierMeta.meaning}` : (tier ?? "לא ידוע")}
                </Row>
                <Row label="רמת אוטונומיה">
                  {autonomy ? AUTONOMY_HE[autonomy] ?? autonomy : "לא ידוע"}
                </Row>
                <Row label="מצב חירום">
                  {!canManage ? (
                    <span className="text-gray-500">לא ידוע — נדרשת הרשאת ניהול כדי לקרוא את מצב החירום</span>
                  ) : crisisQuery.isError ? (
                    <span className="text-gray-500">לא ידוע — קריאת מצב החירום נכשלה</span>
                  ) : crisis === true ? (
                    <span className="text-red-700 font-medium">פעיל — כל פעולות הכתיבה ופתיחת הסשנים חסומות</span>
                  ) : (
                    "כבוי — כתיבה מותרת בכפוף לאישורים"
                  )}
                </Row>
                <Row label="סוגי פעולה מותרים">
                  {entitledTools.length ? (
                    Object.entries(
                      entitledTools.reduce((acc, t) => {
                        const key = t.operation_type ?? "unknown";
                        acc[key] = (acc[key] ?? 0) + 1;
                        return acc;
                      }, {})
                    ).map(([type, count]) => (
                      <span key={type} className="inline-block ml-2">
                        {operationTypeLabel(type)}: {count}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">לא ידוע</span>
                  )}
                </Row>
              </div>
              <p className="text-[11px] text-gray-400 border-t pt-2">
                מדיניות מרחוק: סשן attended בלבד; יעד מפורש בלבד — אין בחירה עצמאית של מכשיר; אין מסלול
                ביצוע כללי ללא כלים מוגבלים ומאושרים.
              </p>
              <p className="text-[11px] text-gray-400">{WRITE_BOUNDARY_HE}</p>
            </Card>

            {/* Evidence */}
            <Card title="ראיות" subtitle="מה שתועד בפועל בריצה הנוכחית — אדם-קריא; הפירוט המלא במסך הריצה">
              {!run ? (
                <EmptyLine>{noRunText("אין ראיות לתצוגה")}</EmptyLine>
              ) : shownActions.length === 0 ? (
                <EmptyLine>לא תועדו פעולות לריצה זו</EmptyLine>
              ) : (
                <ul className="space-y-1.5">
                  {shownActions.map((action) => {
                    const outcome = ACTION_OUTCOME_HE[action.outcome] ?? { label: action.outcome ?? "—", cls: "bg-gray-100 text-gray-600 border-gray-200" };
                    return (
                      <li key={action.id ?? action.sequence_no} className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-[11px] text-gray-400 font-mono">#{action.sequence_no}</span>
                        <Pill cls={outcome.cls}>{outcome.label}</Pill>
                        <span className="text-gray-700">
                          {ACTION_TYPE_LABEL[action.action_type] ?? boundedText(action.action_type, 40) ?? "פעולה"}
                        </span>
                        {action.executed_at && (
                          <span className="text-[11px] text-gray-400 mr-auto">{fmtDateTime(action.executed_at) ?? "—"}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {run && runActions.length > shownActions.length && (
                <p className="text-[11px] text-gray-500">
                  מוצגות {shownActions.length} מתוך {runActions.length} פעולות —{" "}
                  <Link to={`/agent-runs/${run.id}`} className="text-blue-700 hover:underline">לכל הראיות במסך הריצה</Link>
                </p>
              )}
              <div className="border-t pt-2 space-y-1.5">
                <p className="text-[11px] text-gray-400">ראיות אימות חיבורים של העובד:</p>
                {connections.length === 0 ? (
                  <EmptyLine>לא הוגדרו חיבורים לעובד — אין ראיות אימות</EmptyLine>
                ) : (
                  <ul className="space-y-1">
                    {connections.map((c) => {
                      const meta = CONNECTION_STATUS_META[c.last_test_status] ?? null;
                      return (
                        <li key={c.id} className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="font-medium text-gray-800">{c.display_name || c.system_type}</span>
                          {meta ? (
                            <Pill cls={meta.cls}>{meta.label}</Pill>
                          ) : (
                            <Pill cls="bg-gray-100 text-gray-500 border-gray-200">טרם נבדק</Pill>
                          )}
                          {c.last_tested_at && (
                            <span className="text-[11px] text-gray-400 mr-auto">נבדק: {fmtDateTime(c.last_tested_at) ?? "—"}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </Card>

            {/* Verified completion */}
            <Card title="מצב השלמה מאומת" subtitle="לפי יומן הניסיון המבוקר — אימות טכנאי בלבד נחשב חיובי">
              {experienceQuery.isError ? (
                <EmptyLine>מצב ההשלמה המאומת אינו ידוע — טעינת הראיות המבוקרות נכשלה</EmptyLine>
              ) : experienceQuery.isLoading ? (
                <EmptyLine>טוען ראיות השלמה...</EmptyLine>
              ) : !latestCompletion ? (
                <EmptyLine>אין עדיין עבודות שהסתיימו לתיעוד</EmptyLine>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Pill cls={latestCompletion.meta.cls}>
                      {latestCompletion.meta.icon} {latestCompletion.meta.label}
                    </Pill>
                    {experienceEntries[0]?.ticket?.ticket_number != null && (
                      <span className="text-gray-600">
                        {`קריאה #${experienceEntries[0].ticket.ticket_number}${experienceEntries[0].ticket.title ? ` · ${experienceEntries[0].ticket.title}` : ""}`}
                      </span>
                    )}
                    {experienceEntries[0]?.occurred_at && (
                      <span className="text-[11px] text-gray-400 mr-auto">{fmtDateTime(experienceEntries[0].occurred_at)}</span>
                    )}
                  </div>
                  <Row label="בסיס האימות">
                    {latestCompletion.verified
                      ? latestCompletion.basisLabel ?? "אימות מתועד"
                      : "טרם תועד אימות — אין בסיס אימות מאושר"}
                  </Row>
                  {latestCompletion.evidence && <Row label="ראיות אימות">{latestCompletion.evidence}</Row>}
                  {latestCompletion.summary && <Row label="סיכום">{latestCompletion.summary}</Row>}
                  <div className="flex gap-3 text-xs">
                    {experienceEntries[0]?.provenance?.run_id && (
                      <Link to={`/agent-runs/${experienceEntries[0].provenance.run_id}`} className="text-blue-600 hover:underline">לריצה</Link>
                    )}
                    {experienceEntries[0]?.provenance?.ticket_id && (
                      <Link to={`/tickets/${experienceEntries[0].provenance.ticket_id}`} className="text-blue-600 hover:underline">לקריאה</Link>
                    )}
                  </div>
                  {experienceSummary && (
                    <p className="text-[11px] text-gray-500 border-t pt-2">
                      {`ב-${experienceSummary.significant_entries ?? 0} העבודות האחרונות: ${experienceSummary.verified_successes ?? 0} אומתו · ${experienceSummary.unverified_completions ?? 0} הושלמו ללא אימות · ${experienceSummary.failures ?? 0} נכשלו · ${experienceSummary.cancellations ?? 0} בוטלו`}
                    </p>
                  )}
                </div>
              )}
            </Card>
          </div>

          {/* Stored per-session execution evidence (write gate, verification, credential lifecycle) */}
          <OperationalSessions known={opSessions.known} sessions={opSessions.sessions} shown={shownSessions} />

          {/* Audited work history (existing employee experience component) */}
          <ExperienceTimeline
            runs={terminalRuns}
            isLoading={historyQuery.isLoading}
            isError={historyQuery.isError}
          />

          {/* §4.1/§4.2/§4.3 connection-profile contract, stored profile or DEV fixture, record-only */}
          <RemoteConnectionContractPanel agentId={activeId} canManage={canManage} />

          <p className="text-[11px] text-gray-400">
            {run
              ? `מבוסס על הריצה הנוכחית (${headlineStatus ?? "—"}) · עודכן: ${fmtDateTime(workspace.current_work?.updated_at) ?? "—"}`
              : runUnknown
              ? "מצב הריצה הנוכחית אינו ידוע — טעינת פרטי הריצה נכשלה"
              : runPending
              ? "טוען את מצב הריצה…"
              : "אין ריצה פעילה לעובד זה"}
          </p>
        </>
      )}
    </div>
  );
}
